import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface PageSEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
}

export default function PageSEO({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  jsonLd,
  noIndex = false,
}: PageSEOProps) {
  const location = useLocation();
  const siteUrl = typeof import.meta !== 'undefined' && (import.meta as Record<string, unknown>).env
    ? ((import.meta as Record<string, Record<string, string>>).env?.VITE_SITE_URL || '')
    : '';

  useEffect(() => {
    const fullCanonical = canonicalPath ? `${siteUrl}${canonicalPath}` : `${siteUrl}${location.pathname}`;

    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', fullCanonical, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    const existingCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (existingCanonical) {
      existingCanonical.setAttribute('href', fullCanonical);
    } else {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', fullCanonical);
      document.head.appendChild(link);
    }

    if (noIndex) {
      const existingRobots = document.querySelector('meta[name="robots"]');
      if (existingRobots) {
        existingRobots.setAttribute('content', 'noindex, nofollow');
      } else {
        setMeta('robots', 'noindex, nofollow');
      }
    } else {
      const existingRobots = document.querySelector('meta[name="robots"]');
      if (existingRobots) existingRobots.remove();
    }

    const scriptIds: string[] = [];
    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema, idx) => {
        const scriptId = `json-ld-${idx}`;
        scriptIds.push(scriptId);
        let script = document.getElementById(scriptId) as HTMLScriptElement | null;
        if (!script) {
          script = document.createElement('script');
          script.type = 'application/ld+json';
          script.id = scriptId;
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(schema);
      });
    }

    return () => {
      const ids = scriptIds;
      ids.forEach((id) => {
        const script = document.getElementById(id);
        if (script) script.remove();
      });
    };
  }, [title, description, keywords, canonicalPath, ogType, jsonLd, noIndex, siteUrl, location.pathname]);

  return null;
}